import React, { useMemo } from "react";
import { isHtmlNote, sanitizeNoteHtml } from "../utils/noteHtml";

// Typography for HTML installer notes (light + dark): steps get accent markers, headings and
// <strong> stand out, <blockquote> becomes a red "critical" callout and <mark> a highlight.
const HTML_NOTE_STYLES = [
  "leading-relaxed",
  "[&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&>*:first-child]:mt-0",
  "[&_h1]:mb-2 [&_h1]:mt-4 [&_h1]:text-base [&_h1]:font-bold [&_h1]:text-gray-900 dark:[&_h1]:text-white",
  "[&_h2]:mb-2 [&_h2]:mt-4 [&_h2]:text-[15px] [&_h2]:font-bold [&_h2]:text-gray-900 dark:[&_h2]:text-white",
  "[&_h3]:mb-1.5 [&_h3]:mt-3 [&_h3]:text-sm [&_h3]:font-bold [&_h3]:uppercase [&_h3]:tracking-[0.08em] [&_h3]:text-red-600 dark:[&_h3]:text-orange-400",
  "[&_h4]:mb-1 [&_h4]:mt-3 [&_h4]:text-sm [&_h4]:font-semibold",
  "[&_strong]:font-semibold [&_b]:font-semibold",
  "[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:space-y-1.5 [&_ol]:pl-5",
  "[&_ul]:my-2 [&_ul]:list-disc [&_ul]:space-y-1 [&_ul]:pl-5",
  "[&_li]:pl-1 [&_li]:marker:font-bold [&_li]:marker:text-red-600 dark:[&_li]:marker:text-orange-400",
  "[&_li>ul]:mt-1 [&_li>ol]:mt-1",
  "[&_blockquote]:my-3 [&_blockquote]:rounded-r-[8px] [&_blockquote]:border-l-[3px] [&_blockquote]:border-rose-500 [&_blockquote]:bg-rose-500/10 [&_blockquote]:px-3 [&_blockquote]:py-2 [&_blockquote]:text-rose-700 dark:[&_blockquote]:text-rose-200",
  "[&_mark]:rounded-[4px] [&_mark]:bg-amber-300/40 [&_mark]:px-1 [&_mark]:text-inherit",
  "[&_a]:underline [&_a]:underline-offset-2",
  "[&_code]:rounded-[4px] [&_code]:bg-black/5 [&_code]:px-1 [&_code]:font-mono [&_code]:text-[12px] dark:[&_code]:bg-white/10",
  "[&_hr]:my-3 [&_hr]:border-current [&_hr]:opacity-20",
  "[&_small]:text-xs [&_small]:opacity-80",
  "[&_table]:my-2 [&_table]:w-full [&_table]:border-collapse [&_table]:text-xs",
  "[&_th]:border [&_th]:border-gray-300 dark:[&_th]:border-[#3a3a3d] [&_th]:px-2 [&_th]:py-1 [&_th]:text-left [&_td]:border [&_td]:border-gray-300 dark:[&_td]:border-[#3a3a3d] [&_td]:px-2 [&_td]:py-1",
].join(" ");

/** An installer note: HTML notes render (allowlist-sanitized) styled, plain text keeps its line breaks. */
const NoteContent = ({ note, className = "" }) => {
  const html = useMemo(() => (isHtmlNote(note) ? sanitizeNoteHtml(note) : null), [note]);
  if (html === null) return <div className={`whitespace-pre-line ${className}`}>{note}</div>;
  return <div className={`${HTML_NOTE_STYLES} ${className}`} dangerouslySetInnerHTML={{ __html: html }} />;
};

export default NoteContent;
